import React, { useEffect, useMemo, useState } from 'react';
import {
   Button,
   Input,
   Typography,
   Table,
   Tabs,
   Card,
   Dropdown,
   Menu,
} from 'antd';
import '../../styles/_dashboard.scss';
import { useNavigate } from 'react-router-dom';
import { shallowEqual, useSelector, useDispatch } from 'react-redux';
import { PatientStatusFilterOptionEnum } from '../../models/PatientModels';
import FilterDropdown from '../../components/FilterDropdown';
import {
   DownOutlined,
   RightOutlined,
   PlusOutlined,
   SearchOutlined,
   CloseCircleFilled,
} from '@ant-design/icons';
import {
   selectPatientResult,
   updatePatientResult,
} from '../../features/patient/patientSlice';
import {
   useFetchTeamOptionsTableDataMutation,
   useFetchTeamOptionsMutation,
   usePatientDetailsMutation,
   useGetPatientStatusOptionsQuery,
} from '../../features/patient/patientService';
import { name } from 'pubsub-js';

const Dashboard = () => {
   const [patientDetails] = usePatientDetailsMutation();
   const dispatch = useDispatch();
   const { Title, Text } = Typography;

   const [selectedTeamOption, setSelectedTeamOption] = useState(null);
   const [selectedStatusOption, setSelectedStatusOption] = useState(null);
   const [searchValue, setSearchValue] = useState('');

   const [options, setOptions] = useState({
      teamOptions: [],
      tableOptions: [],
      orgTableOptions: [],
   });

   useSelector(selectPatientResult, shallowEqual);

   const [fetchTeamOptionsTableData] = useFetchTeamOptionsTableDataMutation();
   const [fetchTeamOptions] = useFetchTeamOptionsMutation();
   const { data: statusOptionsResponse } = useGetPatientStatusOptionsQuery();

   const navigate = useNavigate();

   const handleApiRequest = async (requestFn, requestData) => {
      try {
         const response = await requestFn(requestData);
         return response;
      } catch (error) {
         return error;
      }
   };

   const [dataFetched, setDataFetched] = useState(false);

   const fetchData = async () => {
      if (!dataFetched) {
         try {
            const teamRes = await handleApiRequest(fetchTeamOptions, '');

            setOptions((prev) => ({
               ...prev,
               teamOptions: teamRes?.data?.data || [],
            }));

            setDataFetched(true);
         } catch (error) {
            console.error('Error fetching data:', error);
         }
      }
   };

   useEffect(() => {
      fetchData();
   }, []);

   const statusOptions = useMemo(() => {
      return statusOptionsResponse || [];
   }, [statusOptionsResponse]);

   const handleGlobalSearch = () => {
      navigate('/search-patient');
   };

   const headerTitleStyle = (text) => {
      return <Text className="header-class ">{text}</Text>;
   };

   const detailsStyle = (text) => {
      return (
         <Text strong className="text-color">
            {text}
         </Text>
      );
   };

   const getPatientDetails = async (_id) => {
      try {
         const response = await patientDetails(_id);
         return response;
      } catch (error) {
         return error;
      }
   };

   const handRightClick = async (selectedrecord) => {
      await getPatientDetails(selectedrecord.patientId);
      dispatch(updatePatientResult(selectedrecord.patientId));
      navigate('/patient/' + selectedrecord.patientId, {
         state: {
            record: selectedrecord,
         },
      });
   };

   const columns = [
      {
         title: headerTitleStyle('Name'),
         dataIndex: 'name',
         key: 'name',
         render: (_key, record) => (
            <div className="font-bold">
               {`${record.lastName}, ${record.firstName}`}
            </div>
         ),
      },
      {
         title: headerTitleStyle('HCRx Patient ID#'),
         dataIndex: 'hrxPatientId',
         key: 'hrxPatientId',
         render: (hrxPatientId) => detailsStyle(hrxPatientId),
      },
      {
         title: headerTitleStyle('Medical Record#'),
         dataIndex: 'medicalRecordId',
         key: 'medicalRecordId',
         render: (medicalRecordId) => detailsStyle(medicalRecordId),
      },
      {
         title: '',
         key: 'action',
         render: (_key, record) => (
            <div>
               <RightOutlined onClick={() => handRightClick(record)} />
            </div>
         ),
      },
   ];

   const applyClientSearch = (data, searchQuery) => {
      if (!searchQuery?.trim()) {
         return data;
      }

      const query = searchQuery.trim().toLowerCase();

      return data.filter((option) => {
         const fullName =
            `${option.lastName || ''}, ${option.firstName || ''}`.toLowerCase();
         const hrxPatientId = `${option.hrxPatientId || ''}`.toLowerCase();
         const medicalRecordId = `${option.medicalRecordId || ''}`.toLowerCase();

         return (
            fullName.includes(query) ||
            hrxPatientId.includes(query) ||
            medicalRecordId.includes(query)
         );
      });
   };

   const loadPatients = async ({ team, status, search }) => {
      const request = {
         teamId: team?.teamId ?? null,
         status: status?.value ?? null,
      };

      const tableDataRes = await handleApiRequest(
         fetchTeamOptionsTableData,
         request,
      );
      const apiData = tableDataRes?.data?.data || [];

      const sortedData = [...apiData].sort((a, b) =>
         a.lastName.localeCompare(b.lastName),
      );

      const filteredData = applyClientSearch(sortedData, search || '');

      setOptions((prev) => ({
         ...prev,
         tableOptions: filteredData,
         orgTableOptions: sortedData,
      }));
   };

   const handleSelectTeam = async (opt) => {
      setSelectedTeamOption(opt);

      const admittedStatusOpt = statusOptions.find(i => i.value === PatientStatusFilterOptionEnum.Admitted);
      setSelectedStatusOption(admittedStatusOpt);

      await loadPatients({
         team: opt,
         status: admittedStatusOpt,
         search: searchValue,
      });
   };

   const handleSelectStatus = async (opt) => {
      setSelectedStatusOption(opt);

      const isDischarged =
         opt?.value === PatientStatusFilterOptionEnum.Discharged;

      if (isDischarged) {
         setSelectedTeamOption(null);
      }

      await loadPatients({
         team: isDischarged ? null : selectedTeamOption,
         status: opt,
         search: searchValue,
      });
   };

   const clearTeamSelection = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      setSelectedTeamOption(null);

      await loadPatients({
         team: null,
         status: selectedStatusOption,
         search: searchValue,
      });
   };

   const clearStatusSelection = async (e) => {
      e.preventDefault();
      e.stopPropagation();

      setSelectedStatusOption(null);

      await loadPatients({
         team: selectedTeamOption,
         status: null,
         search: searchValue,
      });
   };

   const handleTableSearch = (e) => {
      const value = e.target.value || '';
      setSearchValue(value);

      const filteredTableOptions = applyClientSearch(options.orgTableOptions, value);

      setOptions((prev) => ({
         ...prev,
         tableOptions: filteredTableOptions,
      }));
   };

   const dropDownStyle = (value) => {
      return value ? '#000000' : '#adb5bd';
   };

   const renderTeamPatient = () => {
      return (
         <div>
            <div className="d-flex justify-content-between flex-wrap gap-3">
               <div className="d-flex" style={{ minWidth: '300px' }}>
                  <div className="d-flex flex-wrap align-items-start mb-2 gap-2">
                     <FilterDropdown
                        options={options?.teamOptions}
                        selectedOption={selectedTeamOption}
                        placeholder="Select a Team"
                        minWidth="250px"
                        onSelect={handleSelectTeam}
                        onClear={clearTeamSelection}
                        dropDownStyle={dropDownStyle}
                     />

                     <FilterDropdown
                        options={statusOptions}
                        selectedOption={selectedStatusOption}
                        placeholder="Select status"
                        minWidth="200px"
                        onSelect={handleSelectStatus}
                        onClear={clearStatusSelection}
                        dropDownStyle={dropDownStyle}
                     />

                     <Button
                        title="Admit New Patient"
                        type="primary"
                        shape="circle"
                        style={{
                           backgroundColor: '#6AC04B',
                           border: 'none',
                        }}
                        className="ms-4 mt-1"
                        size="small"
                        icon={<PlusOutlined />}
                        onClick={() => navigate('/admit_patient')}
                     />
                  </div>
               </div>

               <div className="flex-grow-1" />
               <div className="flex-grow-1" />
               <div className="flex-grow-1" />

               <div
                  className="d-flex justify-content-end flex-grow-1"
                  style={{ minWidth: '300px' }}
               >
                  <Input
                     className="input-container-dash w-100"
                     addonBefore={<SearchOutlined />}
                     placeholder="Search patient"
                     value={searchValue}
                     onChange={handleTableSearch}
                  />
               </div>
            </div>

            <Table
               columns={columns}
               dataSource={options.tableOptions}
               rowKey="patientId"
            />
         </div>
      );
   };

   const tabData = [
      {
         key: '1',
         title: 'Team Patients',
         render: () => {
            return renderTeamPatient();
         },
      },
   ];

   return (
      <div>
         <div className="dashobard-box">
            <Title level={2}>Dashboard</Title>
            <SearchOutlined
               className="search-btn"
               onClick={handleGlobalSearch}
            />
         </div>
         <div className="tab-style">
            <Tabs defaultActiveKey={tabData[0].key}>
               {tabData.map((tab) => (
                  <Tabs.TabPane tab={tab.title} key={tab.key}>
                     <Card className="card-width">{tab.render()}</Card>
                  </Tabs.TabPane>
               ))}
            </Tabs>
         </div>
      </div>
   );
};

export default Dashboard;